import { useRef, useState, ClipboardEvent, KeyboardEvent } from 'react';

interface CodeInputProps {
  fields: number;
  fieldHeight: number;
  fieldWidth: number;
  className?: string;
  onComplete: (value: string) => void;
  onChange?: (value: string) => void;
}

export const CodeInput = ({
  fields,
  fieldHeight,
  fieldWidth,
  className,
  onComplete,
  onChange,
}: CodeInputProps): JSX.Element => {
  const [values, setValues] = useState<string[]>(Array(fields).fill(''));
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const propagate = (next: string[]) => {
    setValues(next);
    const joined = next.join('');
    onChange?.(joined);
    if (joined.length === fields && next.every((v) => v !== '')) {
      onComplete(joined);
    }
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...values];
    next[index] = digit;
    propagate(next);
    if (digit && index < fields - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    const next = Array(fields).fill('');
    for (let i = 0; i < Math.min(text.length, fields); i++) {
      next[i] = text[i];
    }
    propagate(next);
    inputs.current[Math.min(text.length, fields) - 1]?.focus();
  };

  return (
    <div className={className}>
      <div>
        {values.map((value, index) => (
          <input
            key={index}
            ref={(el) => {
              inputs.current[index] = el;
            }}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            style={{ height: fieldHeight, width: fieldWidth }}
          />
        ))}
      </div>
    </div>
  );
};
